/**
 * PDF Evidence Viewer - Display PDF with evidence quote highlighting
 *
 * Features:
 * - Native PDF rendering using react-pdf
 * - Text search highlighting for evidence quotes
 * - Page navigation (jump buttons + scroll)
 * - Zoom controls
 * - Click-to-select entity interaction
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import { docsApi } from '@/lib/api/client'
import { useSessionStore } from '@/stores/sessionStore'
import { usePdfTextHighlights, type EvidenceQuote, type TextHighlight } from './usePdfTextHighlights'
import type { PDFDocumentProxy } from 'pdfjs-dist'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PdfEvidenceViewerProps {
  documentId: string
  evidenceQuotes: EvidenceQuote[]
  selectedEntityId: string | null
  onEvidenceClick: (entityId: string) => void
  className?: string
}

export function PdfEvidenceViewer({
  documentId,
  evidenceQuotes,
  selectedEntityId,
  onEvidenceClick,
  className,
}: PdfEvidenceViewerProps) {
  const { accountId, projectId } = useSessionStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  // PDF state
  const [pdfData, setPdfData] = useState<string | null>(null)
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [scale, setScale] = useState(1.0)
  const [pageHeights, setPageHeights] = useState<Map<number, number>>(new Map())
  const [pageWidths, setPageWidths] = useState<Map<number, number>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Get highlights from hook
  const { highlights, isSearching, error: searchError } = usePdfTextHighlights(
    pdfDoc,
    evidenceQuotes,
    scale,
    pageHeights
  )

  // Load PDF data
  useEffect(() => {
    async function loadPdf() {
      if (!accountId || !projectId || !documentId) {
        setLoading(false)
        setError('Missing account, project, or document ID')
        return
      }

      setLoading(true)
      setError(null)

      try {
        const response = await docsApi.getFile(
          accountId,
          projectId,
          documentId
        )

        if (!response.fileData) {
          setError('No PDF file available for this document')
          return
        }

        setPdfData(response.fileData)
      } catch (err) {
        console.error('Failed to load PDF:', err)
        setError(err instanceof Error ? err.message : 'Failed to load PDF')
      } finally {
        setLoading(false)
      }
    }

    loadPdf()
  }, [accountId, projectId, documentId])

  // Convert base64 to data URL for react-pdf
  const pdfSource = useMemo(() => {
    if (!pdfData) return null
    return { data: atob(pdfData) }
  }, [pdfData])

  const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentProxy) => {
    setNumPages(pdf.numPages)
    setPdfDoc(pdf)
  }, [])

  const onPageLoadSuccess = useCallback((pageNum: number, page: { width: number; height: number }) => {
    setPageHeights(prev => new Map(prev).set(pageNum, page.height))
    setPageWidths(prev => new Map(prev).set(pageNum, page.width))
  }, [])

  // Zoom controls
  const zoomIn = () => setScale(s => Math.min(2.0, s + 0.1))
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.1))
  const fitToWidth = () => setScale(1.0)

  // Scroll to page
  const scrollToPage = useCallback((pageNum: number) => {
    const pageEl = pageRefs.current.get(pageNum)
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  // Scroll to entity evidence
  useEffect(() => {
    if (!selectedEntityId || highlights.size === 0) return

    // Find first highlight for this entity
    for (const [pageNum, pageHighlights] of highlights) {
      const match = pageHighlights.find(h => h.entityId === selectedEntityId)
      if (match) {
        scrollToPage(pageNum)
        // Scroll to specific highlight after page is in view
        setTimeout(() => {
          const el = document.querySelector(`[data-highlight-entity="${selectedEntityId}"]`)
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 300)
        break
      }
    }
  }, [selectedEntityId, highlights, scrollToPage])

  // Render highlight overlay for a page
  const renderHighlights = useCallback((pageNum: number) => {
    const pageHighlights = highlights.get(pageNum) || []
    const pageWidth = (pageWidths.get(pageNum) || 595) * scale
    const pageHeight = (pageHeights.get(pageNum) || 842) * scale

    if (pageHighlights.length === 0) return null

    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        width={pageWidth}
        height={pageHeight}
        style={{ left: 0, top: 0 }}
      >
        {pageHighlights.map((highlight, hIdx) =>
          highlight.rects.map((rect, rIdx) => {
            const isSelected = highlight.entityId === selectedEntityId
            return (
              <rect
                key={`${hIdx}-${rIdx}`}
                data-highlight-entity={highlight.entityId}
                x={rect.x}
                y={rect.y}
                width={rect.width}
                height={rect.height}
                fill={highlight.color}
                fillOpacity={isSelected ? 0.35 : 0.2}
                stroke={highlight.color}
                strokeWidth={isSelected ? 2 : 1}
                className="pointer-events-auto cursor-pointer transition-all"
                onClick={() => onEvidenceClick(highlight.entityId)}
              >
                <title>{`${highlight.entityName} (${highlight.entityType})`}</title>
              </rect>
            )
          })
        )}
      </svg>
    )
  }, [highlights, selectedEntityId, scale, pageWidths, pageHeights, onEvidenceClick])

  // Loading state
  if (loading) {
    return (
      <div className={cn('flex items-center justify-center h-full bg-warm-100', className)}>
        <div className="flex flex-col items-center gap-3 text-text-muted">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span>Loading PDF...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !pdfSource) {
    return (
      <div className={cn('flex items-center justify-center h-full bg-warm-100', className)}>
        <div className="flex flex-col items-center gap-3 text-text-muted">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <span>{error || 'No PDF available'}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col h-full bg-warm-100', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-warm-200 bg-white/95 backdrop-blur sticky top-0 z-10">
        {/* Page navigation */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-sm text-text-muted mr-2">Jump to:</span>
          {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
            <button
              key={pageNum}
              onClick={() => scrollToPage(pageNum)}
              className="w-7 h-7 text-xs rounded hover:bg-warm-100 transition-colors text-text-secondary"
            >
              {pageNum}
            </button>
          ))}
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {isSearching && (
            <span className="text-xs text-text-muted mr-2">Searching...</span>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomOut}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-text-secondary w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomIn}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={fitToWidth}>
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4">
        <div className="flex flex-col items-center gap-4">
          <Document
            file={pdfSource}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-text-muted" />
              </div>
            }
            error={
              <div className="flex items-center justify-center h-96 text-red-500">
                Failed to load PDF
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
              <div
                key={pageNum}
                ref={el => el && pageRefs.current.set(pageNum, el)}
                className="relative shadow-lg bg-white mb-4"
                id={`pdf-page-${pageNum}`}
              >
                <Page
                  pageNumber={pageNum}
                  scale={scale}
                  onLoadSuccess={(page) => onPageLoadSuccess(pageNum, page)}
                  renderTextLayer={true}
                  renderAnnotationLayer={false}
                />
                {renderHighlights(pageNum)}
              </div>
            ))}
          </Document>
        </div>
      </div>

      {/* Legend */}
      {highlights.size > 0 && (
        <div className="flex-shrink-0 px-4 py-2 border-t border-warm-200 bg-warm-50/80">
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <span>Evidence:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#00b894' }} />
              <span>Market</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#0984e3' }} />
              <span>Business</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f9c148' }} />
              <span>Solution</span>
            </div>
            {searchError && (
              <span className="text-red-500 ml-auto">{searchError}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PdfEvidenceViewer
